/**
 * 종목 검색 · 관심종목 · 차트 · 실시간 시세 타입. (16단계)
 *
 * 백엔드 trading/dto 와 1:1로 대응한다.
 * 한쪽을 바꾸면 반드시 다른 쪽도 바꾼다.
 *
 * 추천 관련 타입은 types.ts 에 있다. 파일을 가른 이유는 16단계가
 * 추천과 무관한 별개의 화면이고, 한 파일에 두면 어느 것이 어느 화면의 것인지
 * 알기 어려워지기 때문이다.
 */

/** 검색 결과 한 줄. 백엔드 StockSummaryResponse */
export interface StockSummary {
  id: number;
  symbol: string;
  name: string;
  /** KOSPI | KOSDAQ 등 */
  market: string;
  /** STOCK | ETF | REIT 등 */
  securityType: string;
  /** 선별 대상인지. false 면 지표를 서버가 즉석 계산한다 */
  universe: boolean;
}

/**
 * 지표를 어디서 얻었는지. 백엔드 StockDetailResponse.IndicatorSource
 *
 * 화면 문구가 달라진다. 계산식은 같지만 기준일이 다를 수 있다.
 */
export type IndicatorSource = 'STORED' | 'ON_DEMAND' | 'NONE';

/** 종목 상세. 백엔드 StockDetailResponse */
export interface StockDetail {
  id: number;
  symbol: string;
  name: string;
  nameEn: string | null;
  market: string;
  securityType: string;
  /** ACTIVE | DELISTED | SCHEDULED */
  status: string;
  universe: boolean;
  /** 유니버스에서 빠진 이유. universe 가 false 일 때만 의미가 있다 */
  excludeReason: string | null;
  /**
   * 우리가 분류한 업종. 종목 하나가 둘 이상일 수 있다
   * (효성 = 전력기기·전선 + 철강·화학·정유). 미분류면 빈 배열이다.
   *
   * 2026-09-10 이전에는 단수 sector 였는데 그 값의 출처인 td_stock.sector 가
   * 전 종목 null 이라 화면에 아무것도 안 나왔다. 진짜 분류는 td_sector_map 이다.
   */
  sectors: string[];
  /** DB 에 있는 마지막 일봉의 거래일. 데이터가 얼마나 최신인지를 말한다 */
  latestTradeDate: string | null;
  closePrice: number | null;
  /** 보유한 일봉 수. 지표가 null 인 이유를 화면이 설명할 수 있다 */
  candleCount: number;
  indicator: import('./types').Indicator | null;
  indicatorSource: IndicatorSource;
  /**
   * 시가총액(상장주식수 x 마지막 종가). 평일 21:15 배치가 채운다.
   *   (2026-09-19 에 16:10 에서 옮겼다 - 애프터마켓까지 담은 확정 봉 종가로 계산하려고)
   *
   * 🔴 섹터 화면의 열에서 이리로 옮겨 왔다 (2026-09-12 사용자 요청).
   *   목록에서 매번 볼 숫자가 아니라 종목을 알아볼 때 한 번 보는 숫자다.
   *   아직 못 구했으면 null - 0 으로 두면 "작은 회사" 로 읽힌다.
   */
  marketCap: number | null;
  /** 내 관심종목에 들어 있는지 */
  watched: boolean;
  /**
   * NXT(넥스트레이드) 거래 지원 여부. null 이면 모른다 - 해외 종목이거나 아직 상세를 안 받았다.
   *
   * 🔴 "애프터마켓 거래 가능" 과 같지 않다 (2026-09-19)
   *   NXT 는 2025-03 출범한 대체거래소이고, 2026-09-14 부터 KRX 본장도 16~20시 경쟁매매를 연다.
   *   NXT 미지원 종목도 KRX 애프터마켓에서는 거래될 수 있다. 그래서 배지 글자도 "NXT" 다.
   */
  nxtSupported: boolean | null;
}

/**
 * 차트 한 봉. 백엔드 CandleResponse
 *
 * 시간 축을 두 형태로 받는다. lightweight-charts 가 일봉은 날짜 문자열,
 * 분봉은 UNIX 초를 요구하기 때문이다. 서버가 KST 로 확정해 둘 다 내려준다.
 */
export interface Candle {
  /** yyyy-MM-dd (KST) */
  date: string;
  /** 봉 시작 시각의 UNIX 초 */
  epochSecond: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number | null;
}

/** 토스가 주는 봉 간격. 5분봉·15분봉은 없다 */
/** 봉 단위. 1w · 1mo 는 토스에 없어 서버가 우리 일봉을 묶어 만든다 (2026-09-29 I-70 · CandleAggregator) */
export type CandleInterval = '1d' | '1m' | '1w' | '1mo';

/**
 * 실시간 시세. 백엔드 PriceResponse
 *
 * 🔴 두 출처가 섞여 있다
 *   lastPrice · quotedAt 은 토스가 준 값이고,
 *   prevClose 이하는 우리가 td_daily_candle 로 계산한 값이다.
 *   토스 현재가 API 는 전일종가도 등락률도 주지 않는다.
 *
 *   그래서 prevCloseDate 를 반드시 함께 보여준다.
 *   일봉 수집이 밀리면 등락 기준일이 어제가 아니라 그저께가 된다.
 */
export interface Price {
  stockId: number;
  symbol: string;
  /** 거래소. KOSPI · KOSDAQ · 나스닥 등. 화면이 종목코드 옆에 찍는다 (2026-09-02) */
  market: string | null;
  lastPrice: number;
  /** 데이터 시각. 체결이 없으면 null */
  quotedAt: string | null;
  prevClose: number | null;
  /** 등락이 무엇 대비인지 */
  prevCloseDate: string | null;
  change: number | null;
  changeRate: number | null;
  /** 장이 닫혀 있으면 true. 화면이 실시간 표시를 끈다 */
  stale: boolean;
}

/**
 * 관심종목 카테고리. 백엔드 WatchlistCategoryResponse (3.10)
 *
 * 화면에서는 관심종목 목록 위의 탭이다.
 * 담긴 종목 수는 서버가 주지 않는다 — 화면이 이미 목록을 갖고 있어 거기서 센다.
 */
export interface WatchlistCategory {
  id: number;
  name: string;
}

/**
 * 관심종목 하나가 속한 카테고리와 그 안에서의 순서.
 * 백엔드 WatchlistItemResponse.CategoryRef (3.10)
 *
 * 순서를 여기가 갖는 이유
 *   같은 종목이 A 에서는 첫 줄, C 에서는 다섯째 줄일 수 있다.
 *   WatchlistItem.sortOrder 는 전체·미분류 탭의 순서다.
 */
export interface WatchlistCategoryRef {
  categoryId: number;
  sortOrder: number;
}

/** 관심종목 한 줄. 백엔드 WatchlistItemResponse */
export interface WatchlistItem {
  watchlistId: number;
  stockId: number;
  symbol: string | null;
  name: string;
  market: string | null;
  securityType: string | null;
  universe: boolean;
  /** 속한 카테고리들. 비어 있으면 미분류다. 한 종목이 여러 곳에 들어갈 수 있다 (3.10) */
  categories: WatchlistCategoryRef[];
  /** 전체·미분류 탭에서의 순서. 카테고리 안의 순서는 categories 가 갖는다 */
  sortOrder: number;
  memo: string | null;
  lastTradeDate: string | null;
  /** 🔴 마지막 일봉의 종가다. 실시간 시세가 아니다 */
  lastClose: number | null;
  prevClose: number | null;
  /**
   * 마지막 일봉의 거래량. (2026-09-10)
   *
   * 🔴 실시간이 아니다. 토스 현재가 API 가 거래량을 주지 않아
   *   등락률과 같은 방식으로 일봉에서 가져온다.
   */
  lastVolume: number | null;
  /** 우리가 분류한 업종. 둘 이상일 수 있고 미분류면 빈 배열이다 */
  sectors: string[];
}

/**
 * 저점매수 가격 알림 한 건. 백엔드 PriceAlertResponse. (2026-08-20)
 *
 * 🔴 알리기만 한다. 매수는 사용자가 토스 앱에서 직접 한다.
 */
export type AlertDirection = 'BELOW' | 'ABOVE';

export interface PriceAlert {
  /** 알림 id. 한 종목에 여러 개가 걸리므로(V123) 삭제는 이것으로 한다 */
  id: number;
  /** 🔴 BELOW = 이하로 내려오면(저점) · ABOVE = 이상으로 올라오면(목표) */
  direction: AlertDirection;
  /** 화면 표기용 한국어. 백엔드가 준다 */
  directionLabel: string;
  symbol: string | null;
  stockName: string | null;
  market: string | null;
  stockId: number;
  targetPrice: number;
  /** 켜져 있는가. 발동하면 꺼진다 - 재설정해야 다시 켜진다 */
  active: boolean;
  /** 마지막 발동 시각. 없으면 null */
  triggeredAt: string | null;
  /** 발동 당시 현재가. 없으면 null */
  triggeredPrice: number | null;
}

/**
 * 매매일지 한 건. 백엔드 MyTradeResponse (13단계, 2026-08-28)
 *
 * 🔴 AI 모의투자(PaperClosedTrade)와 다른 것이다
 *   이쪽은 사용자가 토스 앱에서 낸 실제 주문의 체결이다.
 */
export interface MyTrade {
  symbol: string;
  stockName: string | null;
  boughtAt: string;
  soldAt: string;
  quantity: number;
  buyPrice: number;
  sellPrice: number;
  /** 매수 체결금액 + 매수 수수료 */
  buyAmount: number;
  /** 매도 체결금액 - 매도 수수료 - 세금 */
  sellAmount: number;
  realizedPnl: number;
  returnPercent: number | null;
  holdingDays: number;
  /** 이 매수와 이어지는 추천이 있었으면 그 시각. 없으면 null */
  recommendedAt: string | null;
  profile: string | null;
  recoTarget: number | null;
  /** 체결 통화 — 단가 · 금액 · 손익 · 추천 목표가 이 통화다(달러를 원으로 찍지 않게 · 2026-10-17 Q23) */
  currency: 'KRW' | 'USD';
}

/** 아직 안 판 매수분. 백엔드 MyOpenLotResponse */
export interface MyOpenLot {
  symbol: string;
  stockName: string | null;
  boughtAt: string;
  quantity: number;
  buyPrice: number;
  buyAmount: number;
  /** 매수 체결 통화 — 단가 · 매수금액이 이 통화다 (2026-10-17 Q23) */
  currency: 'KRW' | 'USD';
}

/**
 * 매매일지 전체. 백엔드 MyTradeSummaryResponse
 *
 * 🔴 손익 합계는 통화별로 따로다 (2026-10-17 Q23) — realizedPnl · recoPnl · selfPnl 은 원화 청산만(원),
 *   …Usd 는 달러 청산만(달러). 원화로 환산해 합치지 않는다(환율 시점이 섞인다). 건수 · 승률은 통화와 무관하게 전부 센다.
 */
export interface MyTradeSummary {
  closed: MyTrade[];
  openLots: MyOpenLot[];
  /** 원화 청산의 순손익 합(원) */
  realizedPnl: number;
  winCount: number;
  lossCount: number;
  /** 청산이 0건이면 null - 0%로 두면 "전패" 로 읽힌다 */
  winRate: number | null;
  recoMatched: number;
  /** 추천과 이어진 원화 청산의 순손익 합(원) */
  recoPnl: number;
  /** 스스로 산 원화 청산의 순손익 합(원) */
  selfPnl: number;
  /** 달러 청산의 순손익 합(달러). 기간 안 달러 청산이 0건이면 null — 그때 달러 줄을 그리지 않는다 */
  realizedPnlUsd: number | null;
  recoPnlUsd: number | null;
  selfPnlUsd: number | null;
}

/** 매매일지 — 산 날 시스템 지표 · 칸별 성적. 백엔드 EntryIndicatorResponse (2026-10-16, docs/impl/150 8번) */
export interface EntryIndicators {
  items: {
    symbol: string;
    stockName: string | null;
    boughtAt: string;
    /** 아직 들고 있으면 null */
    soldAt: string | null;
    /** 산 날 전날까지 120거래일 범위 안 위치(%) · 미국 · 봉 부족이면 null */
    position120: number | null;
    /** 전날 종가 ÷ 20일 평균 − 1 (%) */
    disparity20: number | null;
    /** 그날 저점매수(보수) 감시 대상이었나 */
    watched: boolean;
    realizedPnl: number | null;
  }[];
  buckets: {
    label: string;
    count: number;
    wins: number;
    /** 10건 미만이면 null — 몇 건으로 낸 승률은 우연이 크다 */
    winRate: number | null;
    /** 통화가 섞일 수 있는 칸(미국 포함)은 null */
    pnl: number | null;
  }[];
}

/** 미국 주식 양도세 어림 — 백엔드 UsTaxEstimateResponse (2026-10-16, docs/impl/150 9번) */
export interface UsTaxEstimate {
  years: {
    /** 귀속 연도 = 매도 결제일의 연도 */
    year: number;
    trades: number;
    gainKrw: number;
    lossKrw: number;
    netKrw: number;
    taxableKrw: number;
    estimatedTaxKrw: number;
    /** 환율을 못 찾아 뺀 왕복 수 — 0 이 아니면 어림이 덜 잡혔다 */
    fxMissing: number;
    /** 결제일이 없어 체결일로 대신한 왕복 수 */
    settlementFallback: number;
  }[];
  /** 기본공제(원) — 화면이 숫자를 박지 않고 이 값을 그린다 */
  deduction: number;
  /** 세율(소수 · 0.22 = 22%) */
  taxRate: number;
}

/** 섹터 탭 하나. 백엔드 SectorSummaryResponse (2026-08-23) */
export interface SectorSummary {
  code: string;
  stockCount: number;
}

/**
 * 섹터 안의 종목 한 줄. 백엔드 SectorStockResponse (2026-08-23)
 *
 * 🔴 현재가가 없다. 화면이 15초마다 도는 시세 훅으로 붙인다
 *   응답에 실으면 그 값은 응답 시점에 굳어 화면의 다른 가격과 어긋난다.
 */
export interface SectorStock {
  stockId: number;
  symbol: string;
  name: string;
  /** 거래소. 시세가 없을 때 표시에 쓴다 (2026-09-29 I-78 — 전에는 없어서 코스닥이 KOSPI 로 보였다) */
  market: string;
  /** 상장주식수 x 마지막 종가. 아직 못 구했으면 null - 목록 뒤로 밀린다 */
  marketCap: number | null;
  lastClose: number | null;
  /** 마지막 일봉의 거래량. 실시간이 아니다 (토스 현재가 API 에 없다) */
  lastVolume: number | null;
  universe: boolean;
  /** 최근 60거래일 왕복성(1년 환산). 봉이 모자라면 null (2026-10-05) */
  swing3m: number | null;
  /** 최근 120거래일 왕복성(1년 환산) */
  swing6m: number | null;
}

/**
 * 최신 환율. 백엔드 ExchangeRateResponse 와 1:1 대응이다. (2026-08-20)
 *
 * 🔴 참고용 표시 환율이다. 매시 정각에만 갱신되므로
 *   화면은 fetchedAt 을 함께 보여줘 실시간으로 오해하지 않게 한다.
 */
export interface ExchangeRateInfo {
  baseCurrency: string;
  quoteCurrency: string;
  rate: number;
  /** 우리가 받아온 시각. "몇 시 기준" 표시가 읽는다 */
  fetchedAt: string;
  /**
   * 이 환율이 가리키는 시각. 추이선의 x 축이 읽는다. (2026-09-20, I-66)
   *
   * 🔴 fetchedAt 과 다르다 - 한국은행 백필 행은 fetchedAt 이 전부 "백필을 돌린 날" 이라
   *   그 값으로 그리면 과거 1,384일치가 오늘 한 점에 뭉친다.
   */
  at: string;
  /** TOSS(매시 참고 환율) | ECOS(한국은행 일별 매매기준율) */
  source: string;
}

/**
 * 검색을 시작하는 최소 글자 수. 백엔드 StockQueryService.MIN_QUERY_LENGTH 와 맞춘다.
 *
 * 이 값보다 짧으면 요청 자체를 보내지 않는다. 서버도 빈 배열을 주지만,
 * 한 글자마다 왕복하는 것을 프론트에서 먼저 막는다.
 */
export const MIN_SEARCH_LENGTH = 2;

/** 관심종목 상한. 백엔드 WatchlistService.MAX_PER_MEMBER 와 맞춘다 */
export const MAX_WATCHLIST = 50;

/**
 * 카테고리 상한. 백엔드 WatchlistCategoryService.MAX_PER_MEMBER 와 맞춘다. (3.10)
 *
 * 🔴 3이었다가 2026-09-10 사용자 요청으로 풀었다
 *   그 숫자를 정한 것은 화면이었다 - 탭이 한 줄에 들어가야 하고,
 *   폰(375px)에서 [전체][A][B][C][미분류] 다섯 개가 한 줄에 드는 상한이 3이었다.
 *
 *   그래서 상한을 풀면서 탭을 가로 스크롤로 바꿨다(WatchlistPanel).
 *   한 줄에 들어갈 필요가 없어졌으므로 상한의 근거도 사라졌다.
 *
 *   0 은 "제한 없음" 이다. 화면이 이 값으로 만들기 버튼을 막을지 정하는데,
 *   상수를 지우지 않고 0 으로 둔 이유는 나중에 다시 걸 자리를 남겨두기 위해서다.
 */
export const MAX_WATCHLIST_CATEGORIES = 0;

/** 카테고리 이름 길이 상한. 백엔드 WatchlistCategory.NAME_MAX_LENGTH 와 맞춘다 */
export const WATCHLIST_CATEGORY_NAME_MAX = 10;

/**
 * 차트 기간 선택지 — 두 번째 줄 버튼 (2026-10-09 사용자 요청 "3개월 · 6개월 · 1년 · 3년 옵션도 살려줘 · 기본은 3개월").
 *
 * 기간은 "처음 보이는 범위" 다. 받아 오는 봉은 봉 단위(CHART_INTERVALS 의 count)가 정하고,
 * 기간은 그중 최근 몇 개를 화면에 맞출지만 정한다 — 그래서 기간을 바꿔도 다시 받지 않는다(같은 쿼리 키).
 * 기간 밖 과거는 옆으로 밀어 본다.
 */
export const CHART_PERIODS = [
  { key: '3M', label: '3개월' },
  { key: '6M', label: '6개월' },
  { key: '1Y', label: '1년' },
  { key: '3Y', label: '3년' },
] as const;

export type ChartPeriod = (typeof CHART_PERIODS)[number]['key'];

/**
 * 차트 봉 단위 선택지 — 첫째 줄 버튼 넷 (2026-09-29 I-70 · 사용자 결정 · 미래에셋 방식).
 *
 * 🔴 9/29 전에는 "분봉 · 3개월 · 6개월 · 1년 · 3년" 한 줄이었고 뒤 넷이 전부 일봉(보이는 기간)이라
 *   "일봉이 어디 있나" · "1개월이 월봉이냐" 로 읽혔다. 그래서 봉 단위 버튼으로 바꾸고 기간 버튼을 없앴는데,
 *   10/9 사용자가 기간 버튼도 다시 원해 두 줄로 나눴다 — 윗줄 = 봉 크기, 아랫줄 = 보이는 기간(CHART_PERIODS).
 *
 * count 는 받아 오는 봉 수(서버 상한과 같다 · ChartService)
 *   분봉 390 = 정규장 하루 · 일봉 750 = 약 3년 · 주봉 160 = 약 3년 · 월봉 130 = 가진 만큼 전부(종목마다 3~10년)
 * visible 은 기간별로 처음 보이는 봉 수 · null 이면 전부(분봉 — 기간 줄을 쓰지 않는다)
 *   일봉은 9/29 전 기간 버튼과 같은 수(3개월 60 · 6개월 120 · 1년 250 · 3년 750 — 한 달 약 20거래일)
 *   주봉은 1년 52주 · 월봉은 1년 12달로 센다
 *   🔴 가진 봉이 visible 보다 적으면(신규 상장 · 주봉 3년) 있는 만큼 다 보인다(CandleChart fitContent)
 */
export const CHART_INTERVALS = [
  { interval: '1m' as CandleInterval, count: 390, visible: null, label: '분봉' },
  {
    interval: '1d' as CandleInterval,
    count: 750,
    visible: { '3M': 60, '6M': 120, '1Y': 250, '3Y': 750 },
    label: '일봉',
  },
  {
    interval: '1w' as CandleInterval,
    count: 160,
    visible: { '3M': 13, '6M': 26, '1Y': 52, '3Y': 156 },
    label: '주봉',
  },
  {
    interval: '1mo' as CandleInterval,
    count: 130,
    visible: { '3M': 3, '6M': 6, '1Y': 12, '3Y': 36 },
    label: '월봉',
  },
] as const;

// ── 일자별 시세 (2026-08-28) ──

/** 백엔드 DailyPriceRow 와 1:1. 증권사 앱의 "일자별" 표 한 줄이다. */
export interface DailyPriceRow {
  date: string;
  close: number;
  /** 전일 대비. 첫 상장일 등 직전 봉이 없으면 null */
  change: number | null;
  changeRate: number | null;
  volume: number | null;
}

export interface DailyPriceSlice {
  rows: DailyPriceRow[];
  /** 다음 페이지 커서. 이 날짜 이전을 다시 달라고 하면 이어진다 */
  nextBefore: string | null;
  /** 🔴 최대 1년까지만이다 - 그 아래는 서버가 false 로 끊는다 */
  hasMore: boolean;
}

/**
 * 저점매수 사다리. 백엔드 LadderGuideResponse. (2026-10-05, BACKLOG I-89 · docs/impl/138 18.8)
 *
 * 최근 N거래일 저가~고가 범위의 하위 지점이 1단, 그 아래 spacingAtr x ATR14 씩 2 · 3단.
 * 🔴 기간 · 하위 비율 · 간격은 서버 설정(trading.yml ladder-guide)에서 온다 - 화면 문구에 숫자를 박지 않는다
 */
export interface LadderGuide {
  /** false 면 reason 만 의미가 있다(해외 종목 · 일봉 부족) */
  available: boolean;
  reason: string | null;
  /** 이 날 일봉까지로 계산했다 */
  baseDate: string | null;
  closePrice: number | null;
  atr14: number | null;
  /** 단 간격(ATR 배수) */
  spacingAtr: number | null;
  /** 단 사이 가격 차(원) = atr14 x spacingAtr */
  rungStep: number | null;
  ranges: LadderRange[];
}

export interface LadderRange {
  /** 범위를 잡은 거래일 수(60 = 약 3개월) */
  days: number;
  low: number;
  high: number;
  /** 기준일 종가가 범위의 몇 % 지점인가. 최고 = 최저면 null */
  positionPct: number | null;
  rows: LadderRow[];
}

export interface LadderRow {
  /** 하위 몇 % 지점(30 = 하위 30%) */
  heightPct: number;
  /** ★ "기본 규칙" 줄인가. 표시일 뿐 추천이 아니다 */
  base: boolean;
  /** 1단부터의 가격(원) */
  rungs: number[];
}

/**
 * 주도주 한 줄. 백엔드 LeadingStockResponse. (V128, docs/impl/113)
 *
 * 🔴 SectorStock 과 다른 타입인 이유는 market 이다
 *   섹터 화면은 코스피만 보던 시절에 만들어져 거래소를 안 받는다.
 *   주도주는 코스피·코스닥이 섞이고 기본 정렬이 "코스피 먼저" 라 화면이 알아야 한다.
 */
export interface LeadingStock {
  stockId: number;
  symbol: string;
  name: string;
  market: string | null;
  /** 이 종목이 속한 주도 섹터들. 한 종목이 둘 다일 수 있다 */
  sectorCodes: string[];
  lastClose: number | null;
  lastVolume: number | null;
  /** 상위 N(LEADING_STOCK_LIMIT · 10/9 부터 100)을 자르는 기준. 마지막 일봉의 거래대금이다 */
  tradeAmount: number | null;
  universe: boolean;
  /** 내 관심종목에 이미 담겨 있나. ✏️ 2026-10-05 [관심] 열을 뺐다(사용자) - 화면은 더 쓰지 않는다 */
  watched: boolean;
  /** 최근 60거래일 왕복성(1년 환산 · 모달의 1년 왕복성과 같은 눈금). 봉이 모자라면 null (2026-10-05) */
  swing3m: number | null;
  /** 최근 120거래일 왕복성(1년 환산) */
  swing6m: number | null;
}

/** 관리자가 지정한 주도 섹터. 백엔드 LeadingSectorController.SectorRef */
export interface LeadingSectorRef {
  id: number;
  sectorCode: string;
  sortOrder: number;
  /** 왜 골랐는지. 물음표 안내가 그대로 보여준다 */
  note: string | null;
}
